import { resourcesLanguageFrench } from '@ragu-components/ord-providers';

import type { UserResourcesTranslationMessages } from './userResourcesTranslationMessages';

export const userResourcesLanguageFrench: UserResourcesTranslationMessages = {
  users: {
    name: 'Utilisateurs',
    fields: {
      ...resourcesLanguageFrench.users.fields,
      id: 'ID',
      mappings: 'Mapping',
      email: 'Email',
      password: 'Mot de passe',
      username: "Nom d'utilisateur",
      firstname: 'Prénom',
      middlename: 'Deuxième prénom',
      lastname: 'Nom de famille',
      language: 'Langue',
      timezone: 'Fuseau horaire',
      account_status: 'Statut du Compte',
      account_disabled: 'Désactivé',
      account_enabled: 'Activé',
      enabling: 'Autorisation du compte',
      required_actions: 'Actions requises',
      required_actions_values: {
        change_pass: 'Changer le mot de passe',
        configure_totp: 'Configurer TOTP',
      },
      acl_resources: 'Autorisations',
      last_access_at: 'Dernière connexion',
      password_last_change_at: 'Dernier changement de mot de passe',
      sessionexpire: 'Durée de session',
      acl_labels: { bulkUpdateLabel: 'Labels Acl' },
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
      groups: { name: 'Groupes' },
      totp_enabled: 'TOTP activé',
      totp_last_used_at: 'Dernière utilisation TOTP',
      service_class: 'Service class',
      metadata: {
        identification_number: "Numéro d'identification",
        shared_pause: 'Pause partagée',
        call_forwarding_rules: "Règles de renvoi d'appels",
        can_access_own_recordings: 'Peut accéder à ses propres enregistrements',
        recall_enabled: 'Peut utiliser le service de rappel',
        ai_voice_analysis_inbound: 'Vérification de voix falsifiée sur les appels externes entrants',
        ai_voice_analysis_outbound: 'Vérification de voix falsifiée sur les appels externes sortants',
        stats_enabled: 'Peut accéder au système de statistiques',
      },
      photo: {
        name: 'Photo de profil',
        description: 'Télécharger une photo de profil. La taille maximale est de 1MB.',
        current: 'Photo de profil actuelle',
        too_large: 'Photo de profil trop grande',
        invalid: 'Photo de profil invalide',
      },
      assigned_lcr: 'LCR',
    },
    operations: {
      create: 'Créer un utilisateur',
      delete: "Supprimer l'utilisateur",
      edit: "Modifier l'utilisateur",
      sections: {
        personalData: 'Données personnelles',
        userSettings: 'Paramètres',
        permissions: 'Autorisations',
      },
      genPassword: 'Générer un mot de passe sécurisé',
    },
    helperTexts: {
      sessionexpire:
        "Le délai d'expiration de la session de l'utilisateur, en secondes, utilisé pour expirer automatiquement l'authentification de l'utilisateur auprès de certains services.",
      acl_labels: 'Liste des autorisations ACL pour cet utilisateur',
      recall_enabled:
        "Le service de rappel permettra à l'utilisateur sélectionné de lancer une demande de rappel vers une extension locale, si l'appel vers l'extension échoue",
      identification_number:
        "Numéro d'identification annoncé lorsque l'option 'annoncer l'agent' est activée dans les paramètres de la file d'attente",
      shared_pause:
        "Indique si l'utilisateur peut utiliser le service de rappel automatique (programmer des rappels automatiques pour les appels échoués vers d'autres utilisateurs internes)",
      ai_voice_analysis_inbound:
        "Exécute la vérification IA de la voix de l'interlocuteur distant sur les appels entrants vers cet utilisateur provenant de l'extérieur du PBX. Nécessite que le jeton API Realynx soit configuré sur le domaine",
      ai_voice_analysis_outbound:
        "Exécute la vérification IA de la voix de l'appelé sur les appels externes passés par cet utilisateur. Nécessite que le jeton API Realynx soit configuré sur le domaine",
      stats_enabled:
        "Permet à l'utilisateur d'accéder au système de statistiques. Nécessite que les statistiques soient activées dans les paramètres du domaine",
    },
  },
  owner: {
    name: 'Propriétaire',
  },
  groups: {
    name: 'Groupes',
    fields: {
      id: 'ID',
      description: 'Description',
      name: 'Nom de groupe',
      number: 'Numéro de pickup',
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
      users: 'Utilisateurs',
      acl_labels: 'Étiquettes ACL',
      acl_resources: 'Ressources ACL',
    },
    operations: {
      create: 'Créer un groupe',
      delete: 'Supprimer le groupe',
      edit: 'Modifier le groupe',
      sections: {
        groupsInfo: 'Informations sur le groupe',
        permissions: 'Permissions',
      },
    },
    helperTexts: {
      acl_labels: 'Liste des autorisations ACL pour ce groupe',
      number: "Si défini, le groupe peut être utilisé comme groupe de pickup d'appel",
    },
  },
  extensions: {
    name: 'Extensions',
    fields: {
      id: 'ID',
      number: 'Numéro',
      ringtime: 'Durée de sonnerie',
      user: 'Utilisateur',
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
    },
    operations: {
      create: 'Créer une extension',
      delete: 'Supprimer une extension',
      edit: 'Modifier une extension',
      sections: {
        details: "Détails de l'extension",
      },
    },
    helperTexts: {
      number: "Numéro de poste, celui qu'il faut appeler pour joindre l'appareil",
      ringtime: 'Durée de la sonnerie en secondes',
    },
  },
  sip_identities: {
    name: 'Identités SIP',
    fields: {
      id: 'ID',
      assigned_sip_profile: 'Profil SIP attribué',
      extension: 'Extension',
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
      password: 'Mot de passe SIP',
      username: "Nom d'utilisateur",
      user: 'Utilisateur',
      device_types: "Types d'appareil",
      enum_device_types: {
        mobile: 'Mobile',
        chat: 'Chat',
        desktop: 'Desktop',
        phone: 'Phone',
        web: 'Web',
      },
    },
    operations: {
      create: 'Create a SIP identity',
      delete: 'Delete SIP identity',
      edit: 'Edit SIP identity',
      sections: {
        details: 'SIP identity details',
      },
      genPassword: 'Générer un mot de passe SIP sécurisé',
    },
    helperTexts: {
      username: "Nom d'utilisateur de l'Identité Sip, utilisé pour s'enregistrer dans le système",
      password: "Mot de passe de l'identité Sip, utilisé pour s'enregistrer dans le système",
    },
    errors: {
      usernameAlreadyExists: "Ce nom d'utilisateur est déjà utilisé par une autre identité SIP",
    },
  },
  hunting_groups: {
    name: 'Groupes de hunting',
    fields: {
      id: 'ID',
      name: 'Nom',
      description: 'Description',
      labels: { id: 'Labels', name: 'Labels' },
      owner: 'Propriétaire',
      user_numbers: "Numéros d'utilisateur",
    },
    operations: {
      create: 'Créer un groupe des hunting',
      delete: 'Supprimer un groupe des hunting',
      edit: 'Editer un groupe des hunting',
      sections: {
        details: 'Détails du groupe des hunting',
      },
    },
    helperTexts: {
      owner: "L'utilisateur associé à ce groupe de hunting, qui est également son propriétaire",
    },
  },
  user_numbers: {
    name: "Numéros d'utilisateur",
    fields: {
      id: 'ID',
      description: 'Description',
      labels: { id: 'Labels', name: 'Labels' },
      number: 'Nombre',
      owner: 'Propriétaire',
    },
    operations: {
      create: "Créer un numéro d'utilisateur",
      delete: "Supprimer le numéro d'utilisateur",
      edit: "Modifier le numéro d'utilisateur",
      sections: {
        details: "Détails du numéro d'utilisateur",
      },
    },
    helperTexts: {
      owner: "L'utilisateur associé à ce numéro, qui est également son propriétaire",
    },
  },
  voicemails: {
    ...resourcesLanguageFrench.voicemails,
    name: 'Messagerie vocale',
    settingButtonLabel: 'Configurer',
    fields: {
      labels: { id: 'Labels', name: 'Labels' },
      preferences: {
        enabled: 'Activée',
        pin: 'Code PIN',
        quota: 'Quota (secondes)',
      },
    },
    helperTexts: {
      notConfigured: "Aucun paramètre de messagerie vocale. Activez l'édition pour créer une configuration.",
      settingsNotConfigured:
        'La messagerie vocale n`est pas configurée pour votre compte. Contactez un administrateur pour l`activer',
    },
    actions: {
      enable: 'Configurer les paramètres de messagerie vocale',
    },
    notifications: {
      loading: 'Chargement des paramètres de messagerie vocale...',
      fetchError: 'Impossible de charger les paramètres de messagerie vocale',
      createSuccess: 'Paramètres de messagerie vocale créés',
      updateSuccess: 'Paramètres de messagerie vocale mis à jour',
      updateError: "Impossible d'enregistrer les paramètres de messagerie vocale",
      requestError: 'Erreur lors du chargement dei paramètres de messagerie',
    },
    validation: {
      pin: 'Le code PIN doit contenir 4 à 10 chiffres',
    },
  },
  conferences: {
    name: 'Conférences',
  },
  distribution_lists: {
    name: 'Listes de distribution',
    operations: {
      create: 'Créer une liste de distribution',
      delete: 'Supprimer une liste de distribution',
      edit: 'Editer une liste de distribution',
      sections: {
        details: 'Détails de la liste de distribution',
      },
    },
  },
  contacts: resourcesLanguageFrench.contacts,
  user_import: {
    name: 'Importation des utilisateurs',
    fields: {
      overwrite: 'Ecraser les utilisateurs existants sur le match',
      fileUploadHint: 'Cliquez ou faites glisser un fichier ici pour télécharger un fichier CSV',
    },
    operations: {
      create: 'Créer une importation des utilisateurs',
      delete: 'Supprimer l’importation',
    },
    notifications: {
      created: "Opération d'importation des utilisateurs créée",
    },
  },
  tasks: {
    name: 'Tâches',
    actions: {
      download: 'Télécharger',
      downloadBackup: 'Télécharger la sauvegarde',
    },
    fields: {
      id: 'ID',
      type: 'Type',
      created_at: 'Créé le',
      status: 'Statut',
      statuses: {
        starting: 'Démarrage',
        running: 'En cours',
        error: 'Erreur',
        done: 'Fait',
      },
      result: {
        title: 'Résultat',
        detail: 'Détail',
        backupReady: 'La sauvegarde pour le domaine %{realm} est prête à être téléchargée.',
        backupImportDone: 'Importation terminée',
        backupImportSuccess: 'La sauvegarde a été importée avec succès comme domaine %{realm}.',
      },
    },
    enums: {
      type: {
        users_import: 'Importation des utilisateurs',
        users_export: 'Exportation des utilisateurs',
        backup_import: 'Importation de la sauvegarde',
        backup_export: 'Exportation de la sauvegarde',
        contacts_import: 'Importation des contacts',
        contacts_export: 'Exportation des contacts',
      },
    },
  },
  autoprovisioning: {
    authentication: {
      name: 'Authentification',
      settingButtonLabel: 'Configurer',
      description:
        "Lorsqu'elle est désactivée, les appareils AP peuvent s'auto-provisionner sans identifiants pendant la durée spécifiée.",
      status: { enabled: 'Activée', disabled: 'Désactivée' },
      fields: { duration: 'Durée (secondes)', expires_at: 'Expire le', enabled: 'Statut' },
      disableSection: "Ouvrir la fenêtre d'authentification",
      actions: { enable: 'Activer', disable: 'Désactiver' },
      notifications: {
        enableSuccess: 'Authentification activée',
        enableError: "Échec de l'activation de l'authentification",
        disableSuccess: 'Authentification désactivée',
        disableError: "Échec de la désactivation de l'authentification",
        fetchError: 'Échec du chargement des paramètres',
      },
    },
    settings: {
      name: 'Paramètres',
      fields: {
        outbound_proxy: 'Proxy sortant',
        outbound_backup_proxy: 'Proxy sortant de secours',
      },
      notifications: {
        saveSuccess: 'Paramètres enregistrés',
        saveError: "Échec de l'enregistrement des paramètres",
        fetchError: 'Échec du chargement des paramètres',
      },
    },
  },
};
